import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate, Link } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import SEOHead from 'components/SEOHead';
import { fetchHopHeadBeerPacks } from 'services/firebase/FetchBeerPack';
import { getHopHeadSubscriptionHero } from 'services/firebase/subscriptionHeroService';
import { BeerPack } from 'utils/types/BeerPack';
import type { SubscriptionHero } from 'utils/types/SubscriptionHero';
import MonthBeer from 'components/MonthBeer';
import { analyticsService } from 'services/analytics/analyticsService';
import { seoConfig } from 'utils/seoConfig';

const BENEFITS = [
  'Entrega rápida',
  'Cancelamento a qualquer momento',
  'Só IPAs — lúpulo em primeiro lugar',
];

/** Paleta inspirada no mascote Hop Head: lúpulo verde + magenta */
const HH = {
  ink: '#141414',
  muted: '#546E7A',
  sectionMint: '#E8F5E9',
  borderSoft: '#B2DFDB',
  borderMuted: '#BDBDBD',
  magenta: '#C2185B',
  magentaDark: '#880E4F',
  green: '#2E7D32',
  greenBright: '#43A047',
  cream: '#FAFFFA',
  white: '#ffffff',
  savingsBg: '#E8F5E9',
  savingsInk: '#1B5E20',
} as const;

const calcSavings = (
  original: string | undefined,
  discounted: string | undefined,
): string => {
  if (!original || !discounted) return '0,00';
  const diff =
    (Number(original.replace(',', '.')) -
      Number(discounted.replace(',', '.'))) *
    12;
  return diff.toFixed(2).replace('.', ',');
};

const HopHeadSubscription = () => {
  const [packs, setPacks] = useState<BeerPack[]>([]);
  const [selectedPack, setSelectedPack] = useState<BeerPack | null>(null);
  const [hero, setHero] = useState<SubscriptionHero | null>(null);

  const navigate = useNavigate();
  const { setSelectedPlan } = useSubscription();

  useEffect(() => {
    const loadPacks = async () => {
      const fetchedPacks = await fetchHopHeadBeerPacks();
      setPacks(fetchedPacks);
      if (fetchedPacks.length > 0) {
        setSelectedPack(fetchedPacks[0]);
      }
      analyticsService.trackSubscriptionFlow('personal_form');
    };
    loadPacks();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getHopHeadSubscriptionHero()
      .then(data => {
        if (!cancelled && data) setHero(data);
      })
      .catch(() => {
        if (!cancelled) setHero(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubscription = useCallback(
    (withCup: boolean) => {
      if (!selectedPack) {
        console.error('selectedPack is undefined');
        return;
      }

      const selectedPlan = withCup
        ? {
            type: 'COM COPO',
            price: selectedPack.priceWithCup.discounted,
            originalPrice: selectedPack.priceWithCup.original,
            label: selectedPack.label,
            quantity: 1,
            total: parseFloat(selectedPack.priceWithCup.discounted),
            copo: true,
            pack: selectedPack.label,
            subscriptionID: '',
          }
        : {
            type: '',
            price: selectedPack.priceWithoutCup.discounted,
            originalPrice: selectedPack.priceWithoutCup.original,
            label: selectedPack.label,
            quantity: 1,
            total: parseFloat(selectedPack.priceWithoutCup.discounted),
            copo: false,
            pack: selectedPack.label,
            subscriptionID: '',
          };

      setSelectedPlan(selectedPlan);

      analyticsService.trackCta(
        'subscription_select_plan',
        selectedPack.label,
        {
          subscription_plan: selectedPack.label,
          price: parseFloat(selectedPlan.price),
          source: 'hop_head',
        },
      );
      analyticsService.trackSubscriptionFlow(
        'shipping_options',
        selectedPack.label,
        parseFloat(selectedPlan.price),
      );

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate('/subscription');
    },
    [
      navigate,
      selectedPack?.label,
      selectedPack?.priceWithCup?.discounted,
      selectedPack?.priceWithCup?.original,
      selectedPack?.priceWithoutCup?.discounted,
      selectedPack?.priceWithoutCup?.original,
      setSelectedPlan,
    ],
  );

  const organizationData = {
    '@type': 'Organization',
    name: 'Clube da 42',
    description:
      'Série Hop Head — assinatura de IPAs artesanais do Clube da 42',
    url: seoConfig.site.url,
    logo: seoConfig.site.logo,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Avenida 42',
      addressCountry: 'BR',
    },
    sameAs: [
      'https://www.instagram.com/clubeda42',
      'https://www.facebook.com/clubeda42',
    ],
  };

  const productData = selectedPack
    ? {
        '@type': 'Product',
        name: selectedPack.label,
        description: selectedPack.description,
        image: selectedPack.image,
        brand: { '@type': 'Brand', name: 'Clube da 42' },
        offers: [
          {
            '@type': 'Offer',
            price:
              selectedPack.priceWithCup?.discounted ||
              selectedPack.priceWithoutCup?.discounted,
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'Clube da 42' },
          },
        ],
      }
    : null;

  const savingsWithoutCup = calcSavings(
    selectedPack?.priceWithoutCup.original,
    selectedPack?.priceWithoutCup.discounted,
  );

  const savingsWithCup = calcSavings(
    selectedPack?.priceWithCup.original,
    selectedPack?.priceWithCup.discounted,
  );

  // Renderizado em dois lugares: coluna esquerda (desktop) e inline (mobile)
  const heroSrc = hero?.image ?? '/hop-head-mascot.png';
  const heroAlt =
    hero?.alt ?? 'Hop Head — mascote da série de IPAs do Clube da 42';

  const imageCard = (
    <Box
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        border: `3px solid ${HH.ink}`,
        bgcolor: HH.white,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="img"
        src={heroSrc}
        alt={heroAlt}
        loading="eager"
        sx={{
          width: '100%',
          height: { xs: 'auto', md: '100%' },
          maxHeight: { xs: 360, md: 'none' },
          display: 'block',
          objectFit: { xs: 'contain', md: 'cover' },
          objectPosition: { xs: 'center top', md: 'center center' },
          bgcolor: HH.white,
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        bgcolor: HH.cream,
        backgroundImage:
          'radial-gradient(rgba(20, 20, 20, 0.035) 1px, transparent 1px)',
        backgroundSize: '12px 12px',
      }}
    >
      <SEOHead
        title="Série Hop Head — Assinatura de IPAs | Clube da 42"
        description="Assinatura mensal só com IPAs artesanais: amargor, aroma e lúpulo em evidência. Uma proposta diferente do clube clássico — a série Hop Head do Clube da 42."
        keywords="IPA, cerveja IPA, assinatura IPA, lúpulo, cerveja artesanal, clube da 42, hop head, assinatura cerveja"
        structuredData={organizationData}
      />
      {productData && <SEOHead structuredData={productData} />}

      {/* ── HERO SECTION ──────────────────────────────────────────── */}
      <Box component="section">
        {/* Prova social — badge (centralizado, com padding) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: { xs: 4, sm: 5 },
            mb: 3,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              bgcolor: HH.sectionMint,
              border: `1px solid ${HH.borderSoft}`,
              borderRadius: '999px',
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: HH.green,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: '0.875rem', fontWeight: 500, color: HH.ink }}
            >
              Série Hop Head — só IPAs
            </Typography>
          </Box>
        </Box>

        {/* Layout: coluna única no mobile, duas colunas no desktop — full width */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            minHeight: { md: '68vh' },
          }}
        >
          {/* Coluna esquerda: imagem (só aparece no desktop) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              width: '50%',
              flexShrink: 0,
            }}
          >
            {imageCard}
          </Box>

          {/* Coluna direita: todo o conteúdo */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              px: { xs: 2, sm: 3, md: 5 },
              pb: { xs: 5, md: 6 },
              justifyContent: { md: 'center' },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem' },
                fontWeight: 500,
                color: HH.ink,
                lineHeight: 1.2,
                mb: 1.5,
              }}
            >
              Só IPAs. Todo mês.
              <br />
              <Box component="span" sx={{ color: HH.magenta }}>
                Hop Head — lúpulo sem medo.
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: HH.muted,
                mb: 4,
                lineHeight: 1.5,
              }}
            >
              Uma linha diferente do clube clássico: amargor, aroma e atitude.
            </Typography>

            {/* Imagem — visível só no mobile, entre subtítulo e rating */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
              {imageCard}
            </Box>

            {/* Rating */}
            <Link
              to="/depoimentos"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 500,
                    color: HH.ink,
                    lineHeight: 1,
                  }}
                >
                  4,9
                </Typography>
                <Box sx={{ display: 'flex', gap: '2px' }}>
                  {[0, 1, 2, 3, 4].map(i => (
                    <Box
                      key={i}
                      component="span"
                      sx={{ color: '#FFD700', fontSize: '18px', lineHeight: 1 }}
                    >
                      ★
                    </Box>
                  ))}
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: HH.magenta,
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  87 avaliações
                </Typography>
              </Box>
            </Link>

            {/* Benefícios */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                mb: 4,
              }}
            >
              {BENEFITS.map(benefit => (
                <Box
                  key={benefit}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: HH.magenta,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: HH.white,
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 400,
                      color: HH.ink,
                    }}
                  >
                    {benefit}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTAs */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
              }}
            >
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() =>
                  document
                    .getElementById('planos')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                sx={{
                  bgcolor: HH.magenta,
                  color: HH.white,
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: HH.magentaDark, boxShadow: 'none' },
                }}
              >
                Quero assinar
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => navigate('/depoimentos')}
                sx={{
                  borderColor: HH.magenta,
                  color: HH.magenta,
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: HH.magentaDark,
                    color: HH.magentaDark,
                    bgcolor: 'transparent',
                  },
                }}
              >
                Ver depoimentos
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── PLANOS ────────────────────────────────────────────────── */}
      <Box
        component="section"
        id="planos"
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 5, sm: 6 },
          bgcolor: HH.sectionMint,
          backgroundImage:
            'radial-gradient(rgba(20, 20, 20, 0.04) 1px, transparent 1px)',
          backgroundSize: '10px 10px',
        }}
      >
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.75rem' },
              fontWeight: 500,
              color: HH.ink,
              textAlign: 'center',
              mb: 3,
            }}
          >
            Quantas IPAs você quer por mês?
          </Typography>

          {/* Tabs de pack — sempre em linha horizontal */}
          <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
            {packs.map(pack => {
              const isSelected = selectedPack?.id === pack.id;
              return (
                <Box
                  key={pack.id}
                  component="button"
                  onClick={() => setSelectedPack(pack)}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: isSelected
                      ? `3px solid ${HH.ink}`
                      : `2px solid ${HH.borderMuted}`,
                    bgcolor: isSelected ? HH.magenta : HH.white,
                    color: isSelected ? HH.white : HH.muted,
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontFamily: 'inherit',
                    transition: 'all 0.15s ease',
                    '&:focus-visible': {
                      outline: `2px solid ${HH.magenta}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {pack.label}
                </Box>
              );
            })}
          </Box>

          {/* Cards de plano */}
          <Grid container spacing={2}>
            {/* Sem Copo */}
            <Grid item xs={12} sm={6}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: '16px',
                  border: `1.5px solid ${HH.borderSoft}`,
                  bgcolor: HH.white,
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        textDecoration: 'line-through',
                        color: '#9e9e9e',
                        fontSize: '0.875rem',
                        mb: 0.5,
                      }}
                    >
                      R$ {selectedPack?.priceWithoutCup.original}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: HH.ink,
                        }}
                      >
                        R$
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '2.25rem',
                          fontWeight: 500,
                          color: HH.ink,
                          lineHeight: 1,
                        }}
                      >
                        {selectedPack?.priceWithoutCup.discounted}
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: HH.muted }}>
                        /mês
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        bgcolor: HH.savingsBg,
                        borderRadius: '8px',
                        px: 1.5,
                        py: 0.5,
                        mb: 3,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: HH.savingsInk,
                          fontWeight: 500,
                        }}
                      >
                        Economia de R$ {savingsWithoutCup} em 12 meses
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => handleSubscription(false)}
                      sx={{
                        borderColor: HH.magenta,
                        color: HH.magenta,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 500,
                        py: 1.25,
                        '&:hover': {
                          borderColor: HH.magentaDark,
                          color: HH.magentaDark,
                          bgcolor: 'transparent',
                        },
                      }}
                    >
                      Escolher este plano
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Card "Com copo" ocultado temporariamente para exibir apenas "Sem copo". */}
            {false && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ position: 'relative', pt: '14px', height: '100%' }}>
                  {/* Badge "Mais escolhido" */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: HH.magenta,
                      color: HH.white,
                      px: 2,
                      py: 0.4,
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      zIndex: 1,
                    }}
                  >
                    Mais escolhido
                  </Box>

                  <Card
                    sx={{
                      borderRadius: '16px',
                      border: `2px solid ${HH.magenta}`,
                      bgcolor: HH.white,
                      height: '100%',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            color: HH.magenta,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            mb: 2,
                          }}
                        >
                          Com copo
                        </Typography>

                        <Typography
                          sx={{
                            textDecoration: 'line-through',
                            color: '#9e9e9e',
                            fontSize: '0.875rem',
                            mb: 0.5,
                          }}
                        >
                          R$ {selectedPack?.priceWithCup.original}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: HH.ink,
                            }}
                          >
                            R$
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '2.25rem',
                              fontWeight: 500,
                              color: HH.ink,
                              lineHeight: 1,
                            }}
                          >
                            {selectedPack?.priceWithCup.discounted}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.85rem', color: HH.muted }}
                          >
                            /mês
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            bgcolor: HH.savingsBg,
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                            mb: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: HH.savingsInk,
                              fontWeight: 500,
                            }}
                          >
                            Economia de R$ {savingsWithCup} em 12 meses
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleSubscription(true)}
                          sx={{
                            bgcolor: HH.magenta,
                            color: HH.white,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1.25,
                            boxShadow: 'none',
                            '&:hover': {
                              bgcolor: HH.magentaDark,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          Quero receber minhas cervejas
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      {/* ── CAIXA DO MÊS ─────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: HH.cream,
          py: { xs: 5, sm: 6 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <MonthBeer />
      </Box>

      {/* ── SOBRE A SÉRIE ─────────────────────────────────────────── */}
      <section aria-labelledby="about-hop-head">
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 4, sm: 6 },
            maxWidth: 960,
            mx: 'auto',
          }}
        >
          <Card
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              borderRadius: '16px',
              overflow: 'hidden',
              border: `1px solid ${HH.borderSoft}`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 200, sm: 'auto' },
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src="/hop-head-mascot.png"
                alt="Mascote Hop Head — cone de lúpulo com turban e bigode"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Box
              sx={{
                flex: 2,
                bgcolor: HH.green,
                color: HH.white,
                p: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderLeft: { sm: `4px solid ${HH.ink}` },
              }}
            >
              <Typography
                id="about-hop-head"
                component="h2"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                O que é Hop Head?
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 400,
                  mb: 2,
                  fontStyle: 'italic',
                  opacity: 0.9,
                }}
              >
                A série pra quem quer só IPAs — sem compromisso com o resto.
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                }}
              >
                Enquanto o clube clássico mistura estilos e surpresas, a Hop
                Head é focada: todo mês, duas IPAs artesanais escolhidas pra
                destacar lúpulo, amargor e aroma. Visual forte, identidade
                própria e a mesma entrega que você já conhece do Clube da 42.
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  fontSize: '0.95rem',
                  opacity: 0.9,
                }}
              >
                Lúpulo em primeiro lugar — e cerveja gelada na sua porta.
              </Typography>
            </Box>
          </Card>
        </Box>
      </section>
    </Box>
  );
};

export default HopHeadSubscription;
